import type { OrderDetailsUI } from "@/domain/order/order-details.model"
import { inferCodFromPaymentMethod, resolvePaymentTimingFromOrder } from "@/domain/order/order-payment-timing"
import { resolvePaymentMethodDisplayLabel } from "@/domain/payment/payment-method-display"
import {
  type DeferredPaymentTiming,
  isCodPaymentTiming,
  isPrepaidPaymentTiming,
} from "@/domain/payment/payment-timing"
import { OrderStatus } from "@/types/enums/OrderStatus"
import { PaymentTiming } from "@/types/enums/PaymentTiming"

export type PaymentPresentationVariant = "success" | "warning" | "error" | "neutral"

/** Customer-facing payment outcomes — never expose raw gateway states in UI. */
export const PaymentPresentationStatus = {
  PaymentDue: "Payment Due",
  Paid: "Paid",
  AwaitingPayment: "Awaiting Payment",
  PaymentFailed: "Payment Failed",
  Refunded: "Refunded",
  NoPaymentRequired: "No Payment Required",
} as const

export type PaymentPresentationStatusLabel =
  (typeof PaymentPresentationStatus)[keyof typeof PaymentPresentationStatus]

export type PaymentPresentation = {
  statusLabel: PaymentPresentationStatusLabel | string
  statusVariant: PaymentPresentationVariant
  paymentMethodLabel: string | null
  showGateway: boolean
  gatewayLabel?: string
  showTransactionDetails: boolean
  helperText?: string
  transactionId?: string | null
}

export type PaymentPresentationInput = {
  orderStatus?: OrderStatus
  paymentTiming: PaymentTiming
  isPaid: boolean
  paymentStatus: string
  paymentStatusDisplay: string
  paymentMethod?: string
  paymentTypeDisplay?: string
  paymentId?: string | null
}

const TERMINAL_UNPAID_ORDER_STATUSES = new Set<OrderStatus>([OrderStatus.Cancelled, OrderStatus.Returned])

const PAYMENT_HELPER_TEXT = {
  codUnpaid: "Payment will be collected when your order is delivered.",
  pickupUnpaid: "Payment will be collected when you pick up your order.",
  codPaid: "Payment collected upon delivery.",
  pickupPaid: "Payment collected at pickup.",
  cancelledUnpaid: "No payment is required because this order was cancelled.",
  returnedUnpaid: "No payment is required because this order was returned.",
  refunded: "Payment has been refunded.",
} as const

function effectivePaymentTiming(timing: PaymentTiming, paymentMethod?: string): PaymentTiming {
  if (timing !== PaymentTiming.Unknown) return timing
  if (inferCodFromPaymentMethod(paymentMethod)) return PaymentTiming.AtDelivery
  return PaymentTiming.Unknown
}

function resolvePaymentMethodFromFields(input: PaymentPresentationInput): string | null {
  return resolvePaymentMethodDisplayLabel(input.paymentTypeDisplay, input.paymentMethod)
}

function deferredMethodLabel(timing: DeferredPaymentTiming): string {
  return timing === PaymentTiming.AtDelivery ? "Cash on Delivery" : "Pay at Pickup"
}

function isPaymentObligationWaived(orderStatus: OrderStatus, isPaid: boolean): boolean {
  return !isPaid && TERMINAL_UNPAID_ORDER_STATUSES.has(orderStatus)
}


function resolveGatewayVisibility(
  input: PaymentPresentationInput,
  statusLabel: string
): Pick<PaymentPresentation, "showGateway" | "gatewayLabel" | "showTransactionDetails" | "transactionId"> {
  const u = (input.paymentStatus ?? "").trim().toUpperCase()
  const display = (input.paymentStatusDisplay ?? "").trim()
  const transactionId = (input.paymentId ?? "").trim() || null
  const hasMeaningfulGateway = Boolean(u && u !== "UNKNOWN")
  const hasMeaningfulDisplay = Boolean(display && display !== "Unknown")
  const showGateway = hasMeaningfulGateway || hasMeaningfulDisplay

  return {
    showGateway,
    gatewayLabel: showGateway ? (hasMeaningfulDisplay ? display : statusLabel) : undefined,
    showTransactionDetails: Boolean(transactionId || showGateway),
    transactionId,
  }
}

function resolveNoPaymentRequiredPresentation(
  input: PaymentPresentationInput,
  orderStatus: OrderStatus
): PaymentPresentation {
  const timing = effectivePaymentTiming(input.paymentTiming, input.paymentMethod)
  const methodLabel = isCodPaymentTiming(timing)
    ? deferredMethodLabel(timing)
    : resolvePaymentMethodFromFields(input)

  return {
    statusLabel: PaymentPresentationStatus.NoPaymentRequired,
    statusVariant: "neutral",
    paymentMethodLabel: methodLabel,
    showGateway: false,
    showTransactionDetails: false,
    helperText:
      orderStatus === OrderStatus.Returned
        ? PAYMENT_HELPER_TEXT.returnedUnpaid
        : PAYMENT_HELPER_TEXT.cancelledUnpaid,
  }
}

/** Order status Refunded (a staff-set order status). Refund amounts come only from the refund details endpoint. */
function resolveRefundedPresentation(input: PaymentPresentationInput): PaymentPresentation {
  const statusLabel = PaymentPresentationStatus.Refunded

  return {
    statusLabel,
    statusVariant: "neutral",
    paymentMethodLabel: resolvePaymentMethodFromFields(input),
    helperText: PAYMENT_HELPER_TEXT.refunded,
    ...resolveGatewayVisibility(input, statusLabel),
  }
}

function resolveDeferredPresentation(
  input: PaymentPresentationInput,
  timing: DeferredPaymentTiming
): PaymentPresentation {
  const methodLabel = deferredMethodLabel(timing)
  const isDelivery = timing === PaymentTiming.AtDelivery

  if (input.isPaid) {
    return {
      statusLabel: PaymentPresentationStatus.Paid,
      statusVariant: "success",
      paymentMethodLabel: methodLabel,
      showGateway: false,
      showTransactionDetails: false,
      helperText: isDelivery ? PAYMENT_HELPER_TEXT.codPaid : PAYMENT_HELPER_TEXT.pickupPaid,
    }
  }

  return {
    statusLabel: PaymentPresentationStatus.PaymentDue,
    statusVariant: "warning",
    paymentMethodLabel: methodLabel,
    showGateway: false,
    showTransactionDetails: false,
    helperText: isDelivery ? PAYMENT_HELPER_TEXT.codUnpaid : PAYMENT_HELPER_TEXT.pickupUnpaid,
  }
}

function resolveOnlinePresentation(input: PaymentPresentationInput): PaymentPresentation {
  const u = (input.paymentStatus ?? "").trim().toUpperCase()
  const failed = /FAIL|VOID|DECLIN|ERROR|REJECT/i.test(u) && !/REFUND/i.test(u)
  // A legacy refund encoding on payment_status still means the payment was captured; whether and
  // how much was refunded is never read from payment_status (see order-refund-details).
  const success = input.isPaid || /SUCCESS|CAPTURE|PAID|SETTL|COMPLET|REFUND/i.test(u)
  const pending = /PEND|AUTH|PROCESS|REQUIRE|CREAT|AWAIT/i.test(u)

  let statusLabel: PaymentPresentationStatusLabel
  let statusVariant: PaymentPresentationVariant

  if (success && !failed) {
    statusLabel = PaymentPresentationStatus.Paid
    statusVariant = "success"
  } else if (failed) {
    statusLabel = PaymentPresentationStatus.PaymentFailed
    statusVariant = "error"
  } else if (pending) {
    statusLabel = PaymentPresentationStatus.AwaitingPayment
    statusVariant = "warning"
  } else if (input.isPaid) {
    statusLabel = PaymentPresentationStatus.Paid
    statusVariant = "success"
  } else {
    statusLabel = PaymentPresentationStatus.AwaitingPayment
    statusVariant = "warning"
  }

  return {
    statusLabel,
    statusVariant,
    paymentMethodLabel: resolvePaymentMethodFromFields(input),
    ...resolveGatewayVisibility(input, statusLabel),
  }
}

/**
 * Centralized payment presentation for order details UI.
 * Considers both order status and payment state — components render presentation data only.
 */
export function resolvePaymentPresentation(input: PaymentPresentationInput): PaymentPresentation {
  const orderStatus = input.orderStatus ?? OrderStatus.Unknown

  if (orderStatus === OrderStatus.Refunded) {
    return resolveRefundedPresentation(input)
  }
  if (isPaymentObligationWaived(orderStatus, input.isPaid)) {
    return resolveNoPaymentRequiredPresentation(input, orderStatus)
  }

  const timing = effectivePaymentTiming(input.paymentTiming, input.paymentMethod)

  if (isCodPaymentTiming(timing)) {
    return resolveDeferredPresentation(input, timing)
  }

  if (isPrepaidPaymentTiming(timing) || timing === PaymentTiming.Unknown) {
    return resolveOnlinePresentation(input)
  }

  return resolveOnlinePresentation(input)
}

export function resolvePaymentPresentationFromOrder(order: OrderDetailsUI): PaymentPresentation {
  return resolvePaymentPresentation({
    orderStatus: order.orderStatus,
    paymentTiming: order.paymentTiming,
    isPaid: order.isPaid,
    paymentStatus: order.paymentStatus,
    paymentStatusDisplay: order.paymentStatusDisplay,
    paymentMethod: order.paymentMethod,
    paymentTypeDisplay: order.paymentTypeDisplay,
    paymentId: order.paymentId,
  })
}

/** Fields available on my-orders list payloads (OrderListItem / Order). */
export type OrderListPaymentSource = {
  order_status: OrderStatus
  is_paid: boolean
  payment_timing?: unknown
  payment_mode?: string
  payment_status?: string
  payment_method?: string
  primary_payment_method?: string
  order_payment_details?: { payment_type?: unknown } | null
}

/** Same payment presentation rules as order details, for list cards. */
export function resolvePaymentPresentationFromListOrder(
  order: OrderListPaymentSource
): PaymentPresentation {
  const paymentMethod = (order.primary_payment_method ?? order.payment_method ?? "").trim()
  const paymentTypeDisplay =
    resolvePaymentMethodDisplayLabel(
      order.order_payment_details?.payment_type,
      paymentMethod
    ) ?? ""

  return resolvePaymentPresentation({
    orderStatus: order.order_status,
    paymentTiming: resolvePaymentTimingFromOrder(order),
    isPaid: order.is_paid,
    paymentStatus: order.payment_status ?? "",
    paymentStatusDisplay: "",
    paymentMethod,
    paymentTypeDisplay,
    paymentId: null,
  })
}

/** Whether gateway chip should render with positive (success-styled) emphasis. */
export function isPaymentGatewayPositive(
  _presentation: PaymentPresentation,
  _paymentStatus: string
): boolean {
  return false
}

export function isPaymentPresentationPaid(presentation: PaymentPresentation): boolean {
  return presentation.statusLabel === PaymentPresentationStatus.Paid
}
